/** Create-event and event-detail dialogs. Events go to the selected account + calendar; guests get Google invites. */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Clock, ExternalLink, Trash2, Users } from 'lucide-react';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toLocalInput, evStart, evEnd, fmtTime } from './calUtils';

export function CreateEventDialog({ at, accountEmail, onClose, onCreated }) {
  const [f, setF] = useState({ summary: '', start: '', duration: 30, guests: '', description: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (at) setF({ summary: '', start: toLocalInput(at), duration: 30, guests: '', description: '' }); }, [at]);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.request('/calendar/events', { method: 'POST', body: JSON.stringify({
        summary: f.summary || '(No title)', start_time: new Date(f.start).toISOString(), duration_minutes: Number(f.duration),
        attendees: f.guests.split(/[,\s]+/).filter(Boolean), description: f.description }) });
      toast.success(`Saved to ${r.account_email}${f.guests ? ' — invites sent' : ''}`); onCreated();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open={!!at} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="cal-create-dialog">
        <DialogHeader><DialogTitle>New event</DialogTitle></DialogHeader>
        <Input autoFocus placeholder="Add title" className="border-0 border-b text-lg shadow-none focus-visible:ring-0" value={f.summary} onChange={set('summary')} data-testid="cal-create-title" />
        <div className="flex gap-2"><Input type="datetime-local" value={f.start} onChange={set('start')} data-testid="cal-create-start" />
          <select className="rounded-md border bg-background px-2 text-sm" value={f.duration} onChange={set('duration')} data-testid="cal-create-duration">
            {[15, 30, 45, 60, 90, 120].map((m) => <option key={m} value={m}>{m} min</option>)}</select></div>
        <Input placeholder="Add guests (emails)" value={f.guests} onChange={set('guests')} data-testid="cal-create-guests" />
        <Textarea rows={3} placeholder="Add description" value={f.description} onChange={set('description')} data-testid="cal-create-description" />
        <p className="text-xs text-muted-foreground" data-testid="cal-create-account">Saving to {accountEmail}</p>
        <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !f.start} className="bg-[#1a73e8] hover:bg-[#1765cc]" data-testid="cal-create-save">{busy ? 'Saving…' : 'Save'}</Button></div>
      </DialogContent>
    </Dialog>
  );
}

export function EventDialog({ ev, onClose, onDeleted }) {
  if (!ev) return null;
  const del = async () => {
    try { await api.request(`/calendar/events/${ev.event_id}`, { method: 'DELETE' }); toast.success('Event deleted'); onDeleted(); } catch (e) { toast.error(e.message); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="cal-event-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><span className="h-3 w-3 rounded-sm bg-[#039be5]" />{ev.summary || '(No title)'}</DialogTitle></DialogHeader>
        <p className="flex items-center gap-2 text-sm"><Clock className="h-4 w-4 text-muted-foreground" />{evStart(ev).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })} · {fmtTime(evStart(ev))} – {fmtTime(evEnd(ev))}</p>
        {ev.attendees?.length > 0 && <p className="flex items-start gap-2 text-sm"><Users className="mt-0.5 h-4 w-4 text-muted-foreground" />{ev.attendees.join(', ')}</p>}
        {ev.description && <p className="whitespace-pre-line text-sm text-muted-foreground">{ev.description}</p>}
        <div className="flex justify-between">
          <Button variant="ghost" size="sm" className="text-destructive" onClick={del} data-testid="cal-event-delete"><Trash2 className="mr-1 h-4 w-4" />Delete</Button>
          {ev.html_link && <a href={ev.html_link} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm text-[#1a73e8]" data-testid="cal-event-open-google">Open in Google Calendar <ExternalLink className="h-3.5 w-3.5" /></a>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
